import React, { createContext, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Home } from 'lucide-react';
import { FullScreenLayout } from '@/components/shared/layout';
import { ROUTES } from '@/lib/constants/routes';
import { Button } from '@/components/shared/ui';
import { normalizeLocaleTag } from '@/lib/i18nLocale';
import { MAIN_MENUS, SUB_MENUS, type MainMenuId, type SubMenuId } from '@/config/adminMenuConfig';
import { getAdminPathForMenu } from './adminShellLayoutUtils';

export type { MainMenuId, SubMenuId };
export { MAIN_MENUS, SUB_MENUS };

/** 좌측 메뉴명과 헤더 타이틀 통일용 */
const AdminPageTitleContext = createContext<string | null>(null);

export function useAdminPageTitle(): string | null {
  return useContext(AdminPageTitleContext);
}

export interface AdminManagerShellProps {
  children: React.ReactNode;
  mainMenu: MainMenuId;
  subMenu: SubMenuId;
  /** 좌측 메뉴 라벨과 맞춘 페이지 타이틀. 전달 시 하위에서 useAdminPageTitle()로 사용 */
  pageTitle?: string | null;
}

/**
 * 관리자 공통 레이아웃: 헤더(메인 탭) + 사이드바(서브 메뉴) + 본문.
 * 메뉴 클릭 시 해당 실제 경로로 이동 (URL 기반).
 */
export default function AdminManagerShell({
  children,
  mainMenu,
  subMenu,
  pageTitle = null,
}: AdminManagerShellProps): React.ReactElement {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('admin');
  const subMenuItems = SUB_MENUS[mainMenu];
  const isRtl =
    normalizeLocaleTag(i18n.resolvedLanguage || i18n.language) === 'ar'
    || (typeof document !== 'undefined' && document.documentElement.dir === 'rtl');

  const handleSubMenuClick = (id: SubMenuId) => {
    navigate(getAdminPathForMenu(mainMenu, id));
  };

  const handleMainMenuClick = (menuId: MainMenuId) => {
    const firstSub = SUB_MENUS[menuId]?.[0]?.id;
    if (firstSub) navigate(getAdminPathForMenu(menuId, firstSub));
  };

  return (
    <AdminPageTitleContext.Provider value={pageTitle ?? null}>
      <FullScreenLayout
        backgroundColor=""
        header={
          <header className="flex-none bg-white shadow-sm">
            <div className="flex items-center h-16 px-4 gap-6">
              <button
                type="button"
                onClick={() => navigate(ROUTES.HOME)}
                className="text-brand-500 text-xl font-bold transition-colors hover:text-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-500/20 rounded"
                aria-label={t('home')}
              >
                {t('siteName')}
              </button>
              <nav className="flex items-center gap-1" role="tablist">
                {MAIN_MENUS.map(({ id }) => (
                  <Button
                    key={id}
                    variant="ghost"
                    role="tab"
                    aria-selected={mainMenu === id}
                    onClick={() => handleMainMenuClick(id)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      mainMenu === id ? 'text-brand-600 font-bold' : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {t(`mainMenus.${id}`)}
                  </Button>
                ))}
              </nav>

              <Button
                variant="ghost"
                size="md"
                onClick={() => navigate(ROUTES.HOME)}
                className="ms-auto flex items-center gap-2 px-3 h-9"
                aria-label={t('home')}
              >
                <Home size={18} className="text-gray-900 hover:text-brand-500" />
                <span className="text-sm font-medium text-gray-700 hover:text-brand-600">{t('home')}</span>
              </Button>
            </div>
          </header>
        }
      >
        <div className="flex flex-1 min-h-0 relative">
          <aside className={`admin-manager-sidebar fixed top-16 bottom-0 z-10 w-48 bg-white border-gray-200 flex flex-col py-2 ${
            isRtl ? 'right-0 border-l' : 'left-0 border-r'
          }`}>
            {subMenuItems.map(({ id, icon: Icon }) => {
              const isActive = subMenu === id;
              return (
                <Button
                  key={id}
                  variant="ghost"
                  onClick={() => handleSubMenuClick(id)}
                  className={`w-full flex items-center gap-3 px-6 py-3 text-start text-sm transition-colors relative justify-start ${
                    isActive ? 'bg-brand-50 text-brand-700 font-medium' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  {isActive && (
                    <div className={`admin-manager-active-indicator absolute top-0 bottom-0 w-1 bg-brand-600 ${
                      isRtl ? 'right-0 rounded-l' : 'left-0 rounded-r'
                    }`} />
                  )}
                  <Icon size={18} className={`flex-shrink-0 ${isActive ? 'text-brand-600' : 'text-gray-500'}`} />
                  {t(`subMenus.${id}`)}
                </Button>
              );
            })}
          </aside>

          <main data-page-scroll-container className="admin-manager-main flex-1 bg-white p-8 min-w-0 overflow-x-auto overflow-y-scroll overscroll-y-contain ms-48">
            <div className="mx-auto max-w-[1460px]">
              {children}
            </div>
          </main>
        </div>
      </FullScreenLayout>
    </AdminPageTitleContext.Provider>
  );
}
